import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CartBannerHeader from "./CartBannerHeader";
import Checkout from "./Checkout";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Modal } from "react-bootstrap";
import { FaPlusCircle, FaEdit } from "react-icons/fa";
import API_ENDPOINTS from "../../api/apiConfig";
import '../../assets/css/theme.css';
import '../../assets/css/cart.css';
import type { Address } from "../../types";

const CartAddressSelection = () => {
  const [addresses, setAddresses]           = useState<Address[]>([]);
  const [loading, setLoading]               = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [error, setError]                   = useState<string>("");
  const [showAddModal, setShowAddModal]     = useState(false);
  const [showEditModal, setShowEditModal]   = useState(false);
  const [editAddress, setEditAddress]       = useState<Address | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("You must be logged in to view addresses.");
        setLoading(false);
        return;
      }
      const res = await fetch(API_ENDPOINTS.GET_USER_ADDRESS, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch addresses");
      const data = await res.json();
      if (Array.isArray(data.addresses) && data.addresses.length > 0) {
        setAddresses(data.addresses);
        // Auto-select the last address by default
        const lastAddr = data.addresses[data.addresses.length - 1];
        setSelectedAddressId(lastAddr.address_id || lastAddr.id);
      } else {
        setAddresses([]);
      }
    } catch {
      setError("Could not load addresses.");
    }
    setLoading(false);
  };

  const handleSelect = (id: number) => setSelectedAddressId(id);

  const handleContinue = () => {
    const selected = addresses.find(
      (addr) => (addr.id || addr.address_id) === selectedAddressId
    );
    if (selected) {
      localStorage.setItem("selectedAddress", JSON.stringify(selected));
      navigate("/payment-method");
    }
  };

  const handleShowAddModal  = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleShowEditModal = (address: Address) => {
    setEditAddress(address);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setEditAddress(null);
    setShowEditModal(false);
  };

  const handleAddressChanged = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditAddress(null);
    setTimeout(() => fetchAddresses(), 1000);
  };

  return (
    <main className="ffc-page">
      <CartBannerHeader currentStep={2} />

      <section className="ffc-body">
        <div className="ffc-container">
          <div className="ffc-layout ffc-layout--single">
            <div>
              <div className="ffc-section-head">
                <h2 className="ffc-section-title">Where should we deliver?</h2>
                {addresses.length > 0 && (
                  <span className="ffc-count">
                    {addresses.length} saved {addresses.length === 1 ? "address" : "addresses"}
                  </span>
                )}
              </div>

              {loading ? (
                <LoadingSpinner />
              ) : error ? (
                <div className="ffc-error">{error}</div>
              ) : addresses.length === 0 ? (
                <div className="ffc-empty">
                  <span className="ffc-empty-icon">
                    <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 21s-7-6.2-7-11a7 7 0 0114 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" />
                    </svg>
                  </span>
                  <h3 className="ffc-empty-title" style={{ fontSize: 32 }}>No saved addresses</h3>
                  <p className="ffc-empty-sub">Add a delivery address to continue.</p>
                  <button type="button" className="ffh-btn" onClick={handleShowAddModal}>
                    Add your first address
                  </button>
                </div>
              ) : (
                <>
                  <div className="ffc-addr-grid">
                    {addresses.map((addr, idx) => {
                      const id         = addr.address_id || addr.id;
                      const key        = id ? `address-${id}` : `address-${idx}`;
                      const isSelected = selectedAddressId === id;

                      return (
                        <div
                          key={key}
                          className={`ffc-select${isSelected ? " is-selected" : ""}`}
                          onClick={() => handleSelect(id)}
                          role="radio"
                          aria-checked={isSelected}
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(id); } }}
                        >
                          <span className="ffc-radio" aria-hidden="true" />

                          <div className="ffc-addr-name">{addr.full_name}</div>
                          <div className="ffc-addr-phone">{addr.contact_no}</div>
                          <div className="ffc-addr-line">
                            {addr.address_line}<br />
                            {(addr.city || addr.city_name)}, {(addr.state || addr.state_name)} — {addr.postal_code}
                          </div>

                          <div className="ffc-addr-foot">
                            {addr.address_type ? <span className="ffc-tag">{addr.address_type}</span> : <span />}
                            <button
                              type="button"
                              className="ffc-edit"
                              onClick={(e) => { e.stopPropagation(); handleShowEditModal(addr); }}
                              title="Edit Address"
                            >
                              <FaEdit /> Edit
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <button type="button" className="ffc-select ffc-select--add" onClick={handleShowAddModal}>
                      <span className="ffc-add-icon"><FaPlusCircle /></span>
                      <span className="ffc-add-text">Add new address</span>
                    </button>
                  </div>

                  <div className="ffc-actions">
                    <button type="button" className="ffh-btn ffh-btn--outline" onClick={() => navigate("/cart")}>
                      ← Back to cart
                    </button>
                    <button
                      type="button"
                      className="ffh-btn"
                      disabled={!selectedAddressId}
                      onClick={handleContinue}
                    >
                      Continue to payment
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Add Address Modal */}
      <Modal show={showAddModal} onHide={handleCloseAddModal} size="lg" centered dialogClassName="ffc-modal-dialog">
        <Modal.Header closeButton className="ffc-modal-head">
          <Modal.Title className="ffc-modal-title">Add new address</Modal.Title>
        </Modal.Header>
        <Modal.Body className="ffc-modal-body">
          <Checkout
            addressOnly={true}
            onAddressAdded={handleAddressChanged}
            onCancel={handleCloseAddModal}
          />
        </Modal.Body>
      </Modal>

      {/* Edit Address Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg" centered dialogClassName="ffc-modal-dialog">
        <Modal.Header closeButton className="ffc-modal-head">
          <Modal.Title className="ffc-modal-title">Edit address</Modal.Title>
        </Modal.Header>
        <Modal.Body className="ffc-modal-body">
          <Checkout
            addressOnly={true}
            editAddress={editAddress}
            onAddressAdded={handleAddressChanged}
            onCancel={handleCloseEditModal}
            key={editAddress ? editAddress.address_id || editAddress.id : "new"}
          />
        </Modal.Body>
      </Modal>
    </main>
  );
};

export default CartAddressSelection;